import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import { MdEdit } from 'react-icons/md';
import { RiCloseLine } from 'react-icons/ri';
import { FiMenu } from 'react-icons/fi';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { useHistory } from 'react-router-dom';
import api from '../../services/api';
import { useRegisterAdded } from '../../hooks/registerAdded';
import { useToast } from '../../hooks/toast';

import SearchField, { SearchFieldHandles } from '../../components/SearchField';
import Drawers from './components/Drawers';

import AcordeaoSimples from './components/AcordeaoSimples';
import AcordeaoPizza from './components/AcordeaoPizza';

import {
  InformativeSpan,
  ActionsWrapper,
  LittleButton,
  ModalWrapper,
  ModalHeader,
  ModalFooter,
  ItemsToOrder,
  OrderList,
  ActivityIndicator,
} from './styles';
import { useAuth } from '../../hooks/auth';

interface GroupDataType {
  id: number;
  ativo: boolean;
  descricao: string;
  ordem: number;
  empresa_id: number;
  imagem: string;
  pausar_venda: boolean;
  tipo: number;
}

interface ItemToOrder {
  id: string;
  content: string;
}

interface ItemProdutoOrder {
  id: string;
  content: string;
  grupo_produto: string;
}

const styles = makeStyles(() =>
  createStyles({
    wrapper: {
      marginTop: '15px',
      paddingBottom: '50px',
    },
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    loading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 999999,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
  }),
);

const reorder = (list: any, startIndex: any, endIndex: any) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const getItemStyle = (isDragging: any, draggableStyle: any) => ({
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  fontSize: 15,
  color: '#757575',
  padding: 16,
  borderBottom: '1px solid #e0e0e0',
  background: isDragging ? '#e3f2fd' : '#fff',
  textTransform: 'uppercase',

  ...draggableStyle,
});

const Produtos: React.FC = () => {
  const searchInputRef = useRef<SearchFieldHandles>(null);

  const { rotinas, findRotinas } = useAuth();
  const history = useHistory();

  const [groups, setGroups] = useState<GroupDataType[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reorderList, setReorderList] = useState([]);

  const [groupReorder, setGroupReorder] = useState<ItemToOrder[]>([]);
  const [productsReorder, setProductReorder] = useState<ItemProdutoOrder[]>([]);
  const [complementosReorder, setComplementosReorder] = useState<ItemToOrder[]>(
    [],
  );
  const [itemComplementosReorder, setItemComplementosReorder] = useState<
    ItemToOrder[]
  >([]);

  const [modalReorderBtnDisable, setModalReorderBtnDisable] = useState(false);

  const formRef = useRef<FormHandles>(null);

  const {
    hasGroupUpdated,
    hasProductUpdated,
    handleSetHasGroupUpdated,
    handleSetHasProductUpdated,
  } = useRegisterAdded();

  const { addToast } = useToast();

  const classes = styles();

  async function loadData(search?: string, filter?: string): Promise<void> {
    const response = await api.get('/grupo', {
      params: {
        search,
        filter,
      },
    });

    const groupsList: GroupDataType[] = [];

    const groupReorderList: any[] = [];

    response.data.map((item: any): void => {
      const groupItem = {
        id: item.id,
        ativo: item.ativo,
        descricao: item.descricao,
        ordem: item.ordem,
        imagem: item.imagem,
        empresa_id: item.empresa_id,
        pausar_venda: !!item.pausar_venda,
        tipo: item.tipo,
      };

      groupsList.push(groupItem);
      groupReorderList.push({
        id: `${item.id}`,
        content: `${item.descricao}`,
      });
    });

    setGroups(groupsList);
    setGroupReorder([...groupReorderList]);
  }

  useEffect(() => {
    if (hasGroupUpdated) {
      loadData();
      handleSetHasGroupUpdated(false);
      return;
    }

    loadData();
  }, [hasGroupUpdated, handleSetHasGroupUpdated]);

  useEffect(() => {
    async function loadReorderData(): Promise<void> {
      const response = await api.get('/grupo/reordenar');
      setReorderList(response.data);
    }

    loadReorderData();
  }, []);

  const onDragEnd = useCallback(
    (result: any, container_name: string) => {
      if (!result.destination) {
        return;
      }

      switch (container_name) {
        case 'grupo': {
          const newOrder: any[] = reorder(
            groupReorder,
            result.source.index,
            result.destination.index,
          );

          setGroupReorder([...newOrder]);
          return;
        }
        case 'produto': {
          const newOrder: any[] = reorder(
            productsReorder,
            result.source.index,
            result.destination.index,
          );

          setProductReorder([...newOrder]);
          return;
        }
        case 'complemento': {
          const newOrder: any[] = reorder(
            complementosReorder,
            result.source.index,
            result.destination.index,
          );

          setComplementosReorder([...newOrder]);
          return;
        }
        case 'item_complemento': {
          const newOrder: any[] = reorder(
            itemComplementosReorder,
            result.source.index,
            result.destination.index,
          );

          setItemComplementosReorder([...newOrder]);
          break;
        }
        default: {
          break;
        }
      }
    },
    [
      groupReorder,
      productsReorder,
      complementosReorder,
      itemComplementosReorder,
    ],
  );

  const handleSubmitOrder = useCallback(async () => {
    try {
      setModalReorderBtnDisable(true);

      await api.put('/grupo/reordenar', {
        grupos: groupReorder,
        produtos: productsReorder,
        complementos: complementosReorder,
        itens_complementos: itemComplementosReorder,
      });

      addToast({
        type: 'success',
        title: 'Ordem atualizadas',
      });

      setModalReorderBtnDisable(false);
      // setModalOpen(false);
      handleSetHasGroupUpdated(true);
      handleSetHasProductUpdated(true);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro ao reordenar',
      });

      setModalReorderBtnDisable(false);
    }
  }, [
    groupReorder,
    productsReorder,
    complementosReorder,
    itemComplementosReorder,
    addToast,
    handleSetHasGroupUpdated,
    handleSetHasProductUpdated,
  ]);

  const handleLoadProductData = useCallback(async (id: string) => {
    const response = await api.get(`/grupo/grupo_produto/${id}`);

    const products: ItemProdutoOrder[] = [];
    response.data.map((item: any) => {
      const { produto } = item;
      products.push({
        id: `${produto.id}`,
        content: produto.descricao,
        grupo_produto: `${item.id}`,
      });
    });

    setProductReorder([...products]);
    setComplementosReorder([]);
    setItemComplementosReorder([]);
  }, []);

  const handleLoadComplemento = useCallback(async (grupo_produto: string) => {
    const response = await api.get(`/complemento/${grupo_produto}`);

    const complementos: ItemToOrder[] = [];
    response.data.map((item: any) => {
      complementos.push({ id: `${item.id}`, content: item.descricao });
    });

    setComplementosReorder([...complementos]);
  }, []);

  const handleLoadItensComplemento = useCallback(
    async (complemento_id: string) => {
      const response = await api.post(`/complemento/item/${complemento_id}`);

      const itens_complemento: ItemToOrder[] = [];
      response.data.map((item: any) => {
        itens_complemento.push({ id: `${item.id}`, content: item.descricao });
      });

      setItemComplementosReorder([...itens_complemento]);
    },
    [],
  );

  const handleSubmitSearch = useCallback((data): void => {
    const filter =
      (searchInputRef.current && searchInputRef.current.getFilter()) || 'grupo';
    const search = data.search_field;

    loadData(search, filter);
  }, []);

  const handleModalOpen = (): void => {
    setModalOpen(true);
    setProductReorder([]);
    setComplementosReorder([]);
    setItemComplementosReorder([]);
  };

  const handleModalClose = (): void => {
    setModalOpen(false);
  };

  const hasPermission = useCallback(
    (): boolean => findRotinas('produtos')?.listar || false,
    [findRotinas],
  );

  useEffect(() => {
    if (rotinas && !hasPermission()) {
      addToast({
        type: 'error',
        title: 'Não autorizado.',
        description: 'Você não tem permissão.',
      });
      history.push('/products');
    }
  }, [addToast, hasPermission, history, rotinas]);

  return (
    <Container maxWidth="lg" className={classes.wrapper}>
      {isLoading && (
        <div className={classes.loading}>
          <ActivityIndicator />
        </div>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grid container justify="center">
            <Grid item lg={8} md={10} xs={12}>
              <InformativeSpan>
                Aqui você pode criar e editar seus grupos e produtos além de
                alterar preços e definir disponibilidade para seus itens.
              </InformativeSpan>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Form ref={formRef} onSubmit={handleSubmitSearch}>
            <SearchField
              ref={searchInputRef}
              name="search_field"
              placeholder="Pesquisa"
            />
          </Form>
        </Grid>

        <Grid item xs={12}>
          <Drawers isMateriaPrima={false} isClosed={hasGroupUpdated} />
        </Grid>

        <Grid item xs={12}>
          <ActionsWrapper>
            <LittleButton type="button" onClick={handleModalOpen}>
              <span>
                <MdEdit size={16} />
                Reordenar Grupos e Produtos
              </span>
            </LittleButton>
          </ActionsWrapper>

          <Modal
            className={classes.modal}
            open={modalOpen}
            onClose={handleModalClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
              timeout: 500,
            }}
          >
            <Fade in={modalOpen}>
              <ModalWrapper>
                <ModalHeader>
                  <div>
                    <h1>Reordenar</h1>

                    <button type="button" onClick={handleModalClose}>
                      <RiCloseLine size={30} />
                    </button>
                  </div>

                  <div className="description">
                    <span>
                      Para reordenar seus <b>grupos</b>, clique sobre eles e
                      arraste para posição desejada.
                      <br />
                      Para reordenar os <b>produtos</b> de um grupo, clique no
                      nome do grupo e faça o mesmo procedimento.
                    </span>
                  </div>
                </ModalHeader>

                <ItemsToOrder>
                  <div className="header">
                    <div className="title_section">
                      <h3>Grupos</h3>
                    </div>

                    <div className="title_section">
                      <h3>Produtos</h3>
                    </div>

                    <div className="title_section">
                      <h3>Complementos</h3>
                    </div>

                    <div className="title_section">
                      <h3>Itens Complementos</h3>
                    </div>
                  </div>

                  <div className="content">
                    <OrderList>
                      <DragDropContext
                        onDragEnd={(result: any) => onDragEnd(result, 'grupo')}
                      >
                        <Droppable droppableId="droppableGroup">
                          {(provided: any, snapshot: any) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >
                              {groupReorder.map((item: any, index: any) => (
                                <Draggable
                                  key={item.id}
                                  draggableId={item.id}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      style={getItemStyle(
                                        snapshot.isDragging,
                                        provided.draggableProps.style,
                                      )}
                                      className="item_order"
                                      onClick={() =>
                                        handleLoadProductData(item.id)
                                      }
                                    >
                                      <FiMenu size={15} />
                                      <span style={{ marginLeft: 10 }}>
                                        {item.content}
                                      </span>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </OrderList>

                    <OrderList>
                      <DragDropContext
                        onDragEnd={(result: any) =>
                          onDragEnd(result, 'produto')
                        }
                      >
                        <Droppable droppableId="droppableProduct">
                          {(provided: any, snapshot: any) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >
                              {productsReorder.map((item: any, index: any) => (
                                <Draggable
                                  key={item.id}
                                  draggableId={item.id}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      style={getItemStyle(
                                        snapshot.isDragging,
                                        provided.draggableProps.style,
                                      )}
                                      className="item_order"
                                      onClick={() =>
                                        handleLoadComplemento(
                                          item.grupo_produto,
                                        )
                                      }
                                    >
                                      <FiMenu size={15} />
                                      <span style={{ marginLeft: 10 }}>
                                        {item.content}
                                      </span>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </OrderList>

                    <OrderList>
                      <DragDropContext
                        onDragEnd={(result: any) =>
                          onDragEnd(result, 'complemento')
                        }
                      >
                        <Droppable droppableId="droppableComplementos">
                          {(provided: any, snapshot: any) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >
                              {complementosReorder.map(
                                (item: any, index: any) => (
                                  <Draggable
                                    key={item.id}
                                    draggableId={item.id}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={getItemStyle(
                                          snapshot.isDragging,
                                          provided.draggableProps.style,
                                        )}
                                        className="item_order"
                                        onClick={() =>
                                          handleLoadItensComplemento(item.id)
                                        }
                                      >
                                        <FiMenu size={15} />
                                        <span style={{ marginLeft: 15 }}>
                                          {item.content}
                                        </span>
                                      </div>
                                    )}
                                  </Draggable>
                                ),
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </OrderList>

                    <OrderList>
                      <DragDropContext
                        onDragEnd={(result: any) =>
                          onDragEnd(result, 'item_complemento')
                        }
                      >
                        <Droppable droppableId="droppableItemComplemento">
                          {(provided: any, snapshot: any) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >
                              {itemComplementosReorder.map(
                                (item: any, index: any) => (
                                  <Draggable
                                    key={item.id}
                                    draggableId={item.id}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={getItemStyle(
                                          snapshot.isDragging,
                                          provided.draggableProps.style,
                                        )}
                                        className="item_order"
                                      >
                                        <FiMenu size={15} />
                                        <span style={{ marginLeft: 10 }}>
                                          {item.content}
                                        </span>
                                      </div>
                                    )}
                                  </Draggable>
                                ),
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </OrderList>
                  </div>
                </ItemsToOrder>

                <ModalFooter isDisable={modalReorderBtnDisable}>
                  <button
                    type="button"
                    onClick={handleSubmitOrder}
                    disabled={modalReorderBtnDisable}
                  >
                    Salvar
                  </button>
                </ModalFooter>
              </ModalWrapper>
            </Fade>
          </Modal>
        </Grid>

        {groups.map((item) => {
          if (item.tipo === 1) {
            return (
              <Grid item xs={12} key={item.id}>
                <AcordeaoSimples
                  grupo_id={item.id}
                  descricao={item.descricao}
                  pausar_venda={item.pausar_venda}
                  tipo_produto={1}
                  imagem={item.imagem}
                />
              </Grid>
            );
          }

          if (item.tipo === 3) {
            return (
              <Grid item xs={12} key={item.id}>
                <AcordeaoPizza
                  grupo_id={item.id}
                  descricao={item.descricao}
                  pausar_venda={item.pausar_venda}
                  tipo_produto={3}
                  imagem={item.imagem}
                />
              </Grid>
            );
          }

          return null;
        })}
      </Grid>
    </Container>
  );
};

export default Produtos;
